/* One scoped Now connection; public estimates never count as verified attention. */
(() => {
  "use strict";

  function create(options) {
    const { getContext, onCount, onUnavailable } = options;
    const now = options.now || Date.now;
    const every = options.setInterval || setInterval;
    const clear = options.clearInterval || clearInterval;
    const Socket = options.WebSocket || globalThis.WebSocket;
    let socket = null, timer = null, pending = null, scope = null;
    let generation = 0, lastSentAt = -Infinity, connectedAt = 0, retryAt = 0;
    const sameSession = (a, b) => a && b && a.identity === b.identity
      && a.csrf === b.csrf && a.viewerId === b.viewerId;
    const sameCard = (a, b) => sameSession(a, b) && b.active
      && a.slotId === b.slotId && a.card === b.card;

    function detach() {
      ++generation;
      const previous = socket;
      socket = null;
      pending = null;
      if (previous) previous.close(1000, "Now view closed");
    }

    function unavailable(context) {
      const current = getContext();
      if (context?.slotId && sameCard(context, current)) onUnavailable(current);
    }

    function fail(context, seconds = 60) {
      unavailable(context);
      retryAt = Math.max(retryAt, now() + Math.max(60000, seconds * 1000 + 250));
      detach();
    }

    function send(context) {
      if (!socket || socket.readyState !== 1 || pending || now() - lastSentAt < 6000) return;
      pending = { context, sentAt: now() };
      lastSentAt = now();
      try {
        socket.send(JSON.stringify({ type: "watch", slot_id: context.slotId || null }));
      } catch {
        fail(context);
      }
    }

    function connect(context) {
      if (typeof Socket !== "function") { unavailable(context); return; }
      const epoch = ++generation;
      const url = new URL(options.url);
      url.searchParams.set("channel", "now");
      url.searchParams.set("session_key", context.browserKey);
      let currentSocket;
      try {
        currentSocket = new Socket(url.toString());
      } catch {
        fail(context);
        return;
      }
      socket = currentSocket;
      connectedAt = now();
      lastSentAt = -Infinity;
      const owns = () => generation === epoch && socket === currentSocket;
      currentSocket.onopen = () => {
        if (!owns()) return;
        const current = getContext();
        if (!current.active || !sameSession(scope, current)) { stop(); return; }
        send(current);
      };
      currentSocket.onmessage = (event) => {
        if (!owns()) return;
        const current = getContext();
        if (!current.active || !sameSession(scope, current)) { stop(); return; }
        let message;
        try { message = JSON.parse(event.data); } catch { fail(current); return; }
        if (message?.type === "now.unavailable") {
          if (!Number.isSafeInteger(message.retry_after) || message.retry_after < 1 || message.retry_after > 3600) {
            fail(current); return;
          }
          fail(current, message.retry_after);
          return;
        }
        const expected = pending;
        if (!expected || message?.type !== "now.audience" || message.slot_id !== (expected.context.slotId || null)
          || typeof message.active !== "boolean" || message.watching_is_approximate !== true) {
          fail(current); return;
        }
        if (message.active && (message.watching_available !== true || !Number.isSafeInteger(message.watching)
          || message.watching < 0 || message.watching > 100000)) {
          fail(current); return;
        }
        if (!message.active && (message.watching !== null || message.watching_available !== false)) {
          fail(current); return;
        }
        pending = null;
        if (!sameCard(expected.context, current)) return;
        if (message.active) onCount(message.watching, current);
        else if (current.slotId) onUnavailable(current);
      };
      currentSocket.onerror = () => {
        if (!owns()) return;
        const current = getContext();
        if (!current.active || !sameSession(scope, current)) { stop(); return; }
        fail(current);
      };
      currentSocket.onclose = (event) => {
        if (!owns()) return;
        const current = getContext();
        if (!current.active || !sameSession(scope, current)) { stop(); return; }
        unavailable(current);
        socket = null;
        pending = null;
        ++generation;
        // A normal lifetime renewal costs one admission. Unknown/denied handshakes wait a minute.
        retryAt = Math.max(retryAt, now() + (event.code === 1000 ? 1000 : 60000));
      };
    }

    function tick() {
      const context = getContext();
      if (!context.active) { stop(); return; }
      if (!sameSession(scope, context)) {
        detach();
        retryAt = 0;
        scope = context;
      }
      if (now() < retryAt) { unavailable(context); return; }
      if (!socket) { connect(context); return; }
      if ((socket.readyState === 0 && now() - connectedAt >= 10000)
          || (pending && now() - pending.sentAt >= 10000)) {
        fail(context);
        return;
      }
      send(context);
    }

    function start() {
      if (timer === null) timer = every(tick, 500);
      tick();
    }

    function stop({ markUnavailable = false } = {}) {
      const current = getContext();
      if (markUnavailable && sameSession(scope, current) && current.slotId && current.card) onUnavailable(current);
      if (timer !== null) clear(timer);
      timer = null;
      detach();
    }

    return { start, stop };
  }

  if (typeof module !== "undefined" && module.exports) module.exports = { create };
  else globalThis.LouderrNowAudience = { create };
})();
